/**
 * View-model helpers for the Appeal Details modal.
 */

import { AppealDetailDetails } from "../../../../data-model/appealDetail";
import { formatTimestampToDisplay } from "../../../../pages/pharmaOncologyAppeals/table/utils/formatDobToDisplay";

/** Display fields for the Appeal Details modal. */
export interface AppealDetailsViewModel {
  fromDelivery: string;
  toAppeal: string;
  appealReason: string;
}

/**
 * Reads a string field from the appeal detail record.
 *
 * @param record - Appeal record from `pharmacy/appeal/detail`
 * @param key - Field name on the appeal row
 * @returns Trimmed string value or empty string
 */
const readAppealField = (
  record: Record<string, unknown>,
  key: string,
): string => {
  const value = record[key];
  if (value == null) {
    return "";
  }

  return String(value).trim();
};

/**
 * Maps `pharmacy/appeal/detail` payload into Appeal Details modal copy.
 *
 * @param details - Parsed detail response from the appeal detail API
 * @returns Label values shown in the Appeal Details modal
 */
export const buildAppealDetailsViewModel = (
  details: AppealDetailDetails,
): AppealDetailsViewModel => {
  const appeal = (details.appeal ?? {}) as Record<string, unknown>;

  const fromDeliveryRaw =
    readAppealField(appeal, "dumped_at") ||
    readAppealField(appeal, "from_delivery") ||
    readAppealField(appeal, "created_at");

  const toAppealRaw =
    readAppealField(appeal, "appeal_sent_at") ||
    readAppealField(appeal, "to_appeal");

  return {
    fromDelivery: formatTimestampToDisplay(fromDeliveryRaw) || "--",
    toAppeal: formatTimestampToDisplay(toAppealRaw) || "--",
    appealReason: readAppealField(appeal, "appeal_reason") || "--",
  };
};
